import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface PaymentListItem {
  id: string; business: string; plan: string; amount: number; status: string; date: string; invoice: string;
}
export interface PaymentSummary { collected: number; pending: number; failed: number; active_subscriptions: number }

interface PaymentsState {
  items: PaymentListItem[];
  summary: PaymentSummary;
  filters: { plans: string[]; statuses: string[] };
  loading: boolean;
  error: string | null;
}

export function usePayments() {
  const [state, setState] = useState<PaymentsState>({
    items: [], summary: { collected: 0, pending: 0, failed: 0, active_subscriptions: 0 },
    filters: { plans: [], statuses: [] }, loading: true, error: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.PAYMENTS, { params: { page: 1, page_size: 100 } });
      const data = res.data?.data ?? res.data;
      setState({ items: data.items, summary: data.summary, filters: data.filters, loading: false, error: null });
    } catch (err: any) {
      setState((s) => ({ ...s, loading: false, error: err?.response?.data?.message || 'Failed to load payments.' }));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

export function usePaymentDetail(id: string | undefined) {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`${API_ENDPOINTS.PAYMENTS}/${id}`);
      setData((res.data?.data ?? res.data) as Record<string, any>);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load payment.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  return { data, loading, error, reload: load };
}
